import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCheckoutCurrency } from '../amount/checkout-currency-react.js';
import { fundingRateIsCurrent } from '../amount/fx.js';
import { connectionFailureCode } from '../customer/connection-error.js';
import { canConnectFundingProfile, fundingProfileActions, returnToFundingProfile } from '../customer/funding-profile-actions.js';
import { fundingNavigationManifest } from '../generated/navigation.js';
import { useFundingNavigation } from '../operations/navigation-react.js';
import { fundingActionRunner } from './checkout-action.js';
import { authenticatedCheckoutEvent, initialCheckoutState } from './checkout-state.js';
import { fundingDisconnectActions } from './disconnect.js';
import { bankFundingContract } from '../bank-transfers/types.js';
import { isDepositOperation } from '../client.js';
import { excludeFundingMethod, forgetFundingCustomer, fundingMethodSupported, rememberFundingCustomer } from '../discovery/discover-checkout.js';
import { useFundingDiscovery } from '../discovery/discovery-react.js';
import { isFundingMethod, isHostedFundingMethod } from '../discovery/policy.js';
import { unavailablePreference } from '../discovery/returning-method.js';
import { operationSettlement } from '../discovery/settlement-asset.js';
import { fundingOperationForCheckout } from '../operations/checkout-operation.js';
import { useFundingFunnel } from '../operations/funnel-react.js';
import { providerFailure } from '../provider-error.js';
import { verifyRequiredFundingAction } from '../verify-required-action.js';
import { useCardCheckout } from './card-checkout-react.js';
import { useCardResultDeadline } from './card-result-deadline-react.js';
import { useCheckoutDisposal } from './checkout-disposal-react.js';
import { createCheckoutIntentActions } from './checkout-intent-actions.js';
import { checkoutProgress, correctFundingAmount, updateFundingAmount } from './checkout-progress.js';
import { fundingQuoteAction } from './fetch-funding-quote.js';
import { fundingLimitActions, handleLimitChallenge, returnFromLimitVerification } from './funding-limit-recovery.js';
import { applyHostedWidgetEvent } from './hosted/widget.js';
import { applyChallengeEvent, applyPaymentEvent } from './payment-lifecycle.js';
import { paymentRecovery } from './payment-recovery.js';
import { flushPaymentReport as flushReport, paymentReport } from './payment-report.js';
import { usePendingVerification } from './pending-verification-react.js';
import { useFundingCheckoutPreview, useFundingQuoteExpiry } from './quote-preview-react.js';
import { useQuoteReview } from './quote-review-react.js';
/** Shared orchestration; platform adapters own actual provider frames. */
export function useFundingCheckout(requested) {
  const {
    client,
    provider,
    surface
  } = requested;
  const cache = useQueryClient();
  const navigation = useFundingNavigation(client, 'deposit', requested.directPurchase ? {
    ...requested.directPurchase.purchase,
    preferredMethod: requested.directPurchase.preferredMethod
  } : undefined);
  const purchaseScope = requested.directPurchase ? JSON.stringify({
    ...requested.directPurchase.purchase,
    preferredMethod: requested.directPurchase.preferredMethod
  }) : 'funding';
  const progress = checkoutProgress(requested.directPurchase ? `${client.progressKey ?? client.cacheKey}:${purchaseScope}` : client.progressKey ?? client.cacheKey);
  const [selected, setSelected] = useState(requested.directPurchase && fundingNavigationManifest.direct_token_purchase.preferred_method === 'entry_method' ? requested.directPurchase.preferredMethod : progress.method);
  const [localOperation, updateOperation] = useState(undefined);
  const {
    quote,
    review,
    setQuote,
    setReview,
    presentQuote,
    approveQuote
  } = useQuoteReview();
  const phase = navigation.checkout;
  const [attemptId, setAttemptId] = useState(null);
  const [paymentSignature, setPaymentSignature] = useState(null);
  const paymentClosed = useRef(false);
  const secondFactor = useRef(false);
  const quoteContinuation = useRef(null);
  const [frameReady, setFrameReady] = useState(false);
  const [challengeUrl, setChallengeUrl] = useState(null);
  const [challengeRetryUrl, setChallengeRetryUrl] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const providerTransaction = useRef(null);
  const [busy, setBusy] = useState(false);
  const [refreshingCustomer, setRefreshingCustomer] = useState(false);
  const [lastProviderFailure, setProviderFailure] = useState(null);
  const [error, setError] = useState(null);
  const active = useRef(null);
  const pendingCreate = useRef(null);
  const pendingReport = useRef(null);
  const pendingCardMount = useRef(null);
  const reporting = useRef(null);
  const [resumeOperationId, setResumeOperationId] = useState(requested.resumeOperationId !== undefined ? requested.resumeOperationId : navigation.operationId ?? null);
  // Historical payments belong to explicit history recovery; only this visit's
  // tracked operation may resume automatically when its modal reopens.
  const discovery = useFundingDiscovery({
    ...requested,
    currency: localOperation?.fiatCurrency ?? requested.currency,
    ...(resumeOperationId === undefined ? {} : {
      resumeOperationId
    })
  });
  const skippedPending = useRef(null);
  const historicalPendingId = discovery.data?.context.pendingId;
  useEffect(() => {
    if (resumeOperationId === null && historicalPendingId && skippedPending.current !== historicalPendingId) {
      skippedPending.current = historicalPendingId;
      client.observeLifecycle?.({
        event: 'pending_resume_skipped',
        reason: 'fresh_deposit'
      });
    }
  }, [client, historicalPendingId, resumeOperationId]);
  if (localOperation === undefined && discovery.data?.pending) updateOperation(discovery.data.pending);
  useCheckoutDisposal(client, active, reporting);
  const methods = [...new Set([...(discovery.data?.methods ?? []), ...(discovery.data?.connectionRequiredMethods ?? [])])].filter(method => fundingMethodSupported(provider, method));
  const operation = localOperation === undefined ? discovery.data?.pending ?? null : localOperation;
  const bankContract = bankFundingContract(selected ?? operation?.method ?? progress.method ?? '');
  const currencyDraft = useCheckoutCurrency(bankContract ? {
    currency: bankContract.currency,
    decimals: 2
  } : requested, progress, operation);
  const {
    currency,
    decimals,
    units: previewUnits,
    setUnits: setPreviewUnits
  } = currencyDraft;
  const options = {
    ...requested,
    ...(operation?.purchase && (!requested.directPurchase || navigation.attempted || !['created', 'action_required'].includes(operation.status) || operation.purchase.caip19 === requested.directPurchase.binding.caip19 && operation.method === requested.directPurchase.preferredMethod) && (operation.method === 'apple_pay' || operation.method === 'google_pay' || operation.method === 'card') ? {
      directPurchase: {
        purchase: operation.purchase,
        binding: operation.purchase,
        preferredMethod: operation.method,
        origin: operation.purchase.origin ?? requested.directPurchase?.origin ?? 'market'
      }
    } : {}),
    currency,
    decimals,
    ...(currencyDraft.fx !== undefined ? {
      fundingFx: currencyDraft.fx
    } : {})
  };
  if (bankContract) delete options.fundingFx;
  const previous = operation && !operation.withdrawal && isFundingMethod(operation.method) ? operation.method : null;
  const method_0 = (operation && !operation.withdrawal && isHostedFundingMethod(operation.method) ? operation.method : null) ?? (selected && methods.includes(selected) ? selected : null) ?? (previous && methods.includes(previous) ? previous : null);
  const pendingId = operation?.id ?? navigation.operationId ?? null;
  const retainedHostedQuote = Boolean(method_0 && isHostedFundingMethod(method_0) && paymentSignature);
  const autoRefresh = phase === 'review' && !retainedHostedQuote && !busy && options.active !== false && (!options.directPurchase || options.directPurchaseEnabled === true);
  useFundingQuoteExpiry(quote, autoRefresh, setReview, () => {
    if (operation) void run(signal => quoteOperation(operation, signal));
  });
  const initialState = initialCheckoutState(operation, discovery.data, method_0, options.authenticationRequested, progress.verificationRequested, Boolean(options.directPurchase));
  const state = phase === 'methods' && !['methods', 'amount'].includes(initialState) ? initialState : phase ?? initialState;
  const amountUnits = previewUnits === undefined ? operation?.receiveTarget?.localUnits ?? operation?.principalUnits ?? null : previewUnits;
  useCardResultDeadline(state === 'payment' && method_0 === 'card' && options.active !== false, attemptId, () => {
    paymentClosed.current = true;
    setPaymentSignature(null);
    setError('payment_page_load_failed');
    client.observeFunnel?.({
      stage: 'payment',
      outcome: 'error',
      method: 'card',
      errorCode: 'payment_result_timeout'
    });
    navigation.transition('payment_uncertain');
  });
  const verificationIntent = useMemo(() => operation && isDepositOperation(operation) ? {
    ...operation,
    settlement: operationSettlement(operation)
  } : null, [operation]);
  usePendingVerification({
    enabled: options.active !== false && phase === 'review' && error === 'verification_pending' && !attemptId,
    operation: verificationIntent,
    operationId: operation?.id ?? null,
    provider,
    active,
    onQuote: presentQuote,
    setError,
    setProviderFailure
  });
  useFundingFunnel(client, {
    stage: discovery.isPending ? 'discovery' : state,
    outcome: discovery.isPending || busy ? 'loading' : error || discovery.isError ? 'error' : 'entered',
    method: method_0,
    operationId: operation?.id ?? null,
    errorCode: error ?? connectionFailureCode(discovery.error) ?? (discovery.isError ? 'provider_unavailable' : null)
  });
  const run = fundingActionRunner({
    active,
    setBusy,
    setError,
    setProviderFailure,
    hasAttempt: () => Boolean(attemptId || paymentSignature || navigation.getSnapshot().attempted),
    onSessionRefresh: options.onSessionRefresh
  });
  function refreshCustomer() {
    setRefreshingCustomer(true);
    void run(async () => {
      const result = await discovery.refetch();
      if (result.error) throw result.error;
    }).finally(() => setRefreshingCustomer(false));
  }
  function setOperation(current) {
    if (current && !isDepositOperation(current)) throw new Error('onramp_direction_mismatch');
    updateOperation(current);
    // Retire a resumed binding, but keep the existing customer discovery when
    // abandoning a newly created local checkout that never resumed an order.
    if (current === null) {
      if (resumeOperationId || discovery.data?.pending) setResumeOperationId(null);
      providerTransaction.current = null;
      setTransactionId(null);
    }
  }
  const cards = useCardCheckout({
    provider,
    navigation,
    operation,
    run,
    onFailure: stage => {
      client.observeFunnel?.({
        stage,
        outcome: 'error',
        method: 'card',
        errorCode: 'provider_unavailable'
      });
    },
    quote: async (current_0, id, signal_0, onReady) => {
      await fundingQuoteAction({
        provider,
        navigation,
        attemptId,
        presentQuote,
        setChallengeUrl,
        providerPaymentMethodId: id
      })(current_0, signal_0, onReady).catch(failure => {
        if (!signal_0.aborted) client.observeFunnel?.({
          stage: 'card_quote',
          outcome: 'error',
          method: 'card',
          errorCode: 'provider_unavailable'
        });
        throw failure;
      });
    }
  });
  const savedCards = discovery.data?.savedCards ?? [];
  const {
    preview,
    canContinueAmount
  } = useFundingCheckoutPreview({
    options,
    method: method_0,
    amountUnits,
    discovery: discovery.data,
    state,
    busy,
    refreshing: refreshingCustomer,
    canUpdateAmount: !attemptId || frameReady,
    ...(cards.card ? {
      providerPaymentMethodId: cards.card.id
    } : {})
  });
  const quoteOperation = fundingQuoteAction({
    provider,
    ...(cards.card ? {
      providerPaymentMethodId: cards.card.id
    } : {}),
    ...(client.observeDiagnostic ? {
      diagnose: client.observeDiagnostic
    } : {}),
    navigation,
    attemptId,
    presentQuote,
    setChallengeUrl
  });
  function flushPaymentReport() {
    return flushReport(client, pendingReport, reporting, () => setError('submission_unknown'));
  }
  function continuation(next, origin) {
    if (next.phase === 'unsupported' && method_0) excludeFundingMethod(provider, method_0);
    const submitted = navigation.getSnapshot().attempted;
    if (origin === 'payment' && !submitted && ['submission_unknown', 'cancelled', 'rejected'].includes(next.phase)) {
      // A frame setup error or dismissal does not prove that the wallet was used.
      if (next.phase === 'cancelled') return;
      paymentClosed.current = true;
      setPaymentSignature(null);
      setError('provider_unavailable');
      navigation.transition('payment_unavailable');
      return;
    }
    const notice = next.phase === 'authorizing' ? 'awaiting_payment' : next.phase === 'reconciling' || next.phase === 'submission_unknown' ? 'submission_unknown' : next.phase === 'challenge_required' ? 'action_required' : next.phase === 'cancelled' ? 'cancelled' : null;
    if (notice) options.onPaymentStatus?.(notice);
    if (origin === 'payment' && !navigation.getSnapshot().attempted && (next.phase === 'unsupported' || next.phase === 'quote_expired')) {
      if (next.phase === 'unsupported' && method_0) {
        paymentClosed.current = true;
        setPaymentSignature(null);
        setAttemptId(null);
        setOperation(null);
        setQuote(null);
        setReview(null);
        setSelected(null);
        navigation.deposit();
        return;
      }
      // Keep the reservation fenced; a non-executable frame is not a payment in progress.
      paymentClosed.current = true;
      setPaymentSignature(null);
      setError(next.phase);
      navigation.transition('payment_unavailable');
      return;
    }
    const recovery = paymentRecovery(next, Boolean(attemptId), origin, quoteContinuation.current !== null, providerTransaction.current !== null);
    const report = paymentReport(operation && attemptId ? {
      operationId: operation.id,
      attemptId
    } : null, next);
    let reported = Promise.resolve();
    if (next.phase === 'transaction_created' || next.phase === 'reconciling') {
      providerTransaction.current = next.transactionId;
      setTransactionId(next.transactionId);
    }
    if (report) {
      navigation.markAttempt();
      pendingReport.current = {
        ...report,
        providerTransactionId: report.providerTransactionId ?? pendingReport.current?.providerTransactionId ?? null
      };
      reported = flushPaymentReport();
    }
    if (recovery === 'status' && attemptId) {
      paymentClosed.current = true;
      setPaymentSignature(null);
      quoteContinuation.current = null;
      active.current?.abort();
      setBusy(false);
    }
    if (recovery === 'ignore') return;
    if ((next.phase === 'widget_closed' || next.phase === 'provider_failed') && recovery === 'amount') {
      if (next.phase === 'provider_failed') {
        paymentClosed.current = true;
        setPaymentSignature(null);
      }
      setBusy(false);
      setError(next.phase === 'provider_failed' ? 'provider_unavailable' : null);
      navigation.transition('edit_amount');
      return;
    }
    if (recovery === 'resume_payment') {
      secondFactor.current = next.phase === 'second_factor_required';
      setError(secondFactor.current ? 'second_factor_required' : null);
      return;
    }
    if (attemptId && operation && (next.phase === 'challenge_required' || next.phase === 'verification_pending'))
      // Initiation advances the operation version; verify against that settled version.
      void reported.then(() => verifyRequiredFundingAction(client, provider, operation.id));
    if (recovery === 'challenge' && next.phase === 'challenge_required') {
      setChallengeUrl(next.url);
      navigation.transition('payment_challenge');
    } else if (recovery === 'quote' && operation) {
      setChallengeUrl(null);
      void run(signal_1 => quoteOperation(operation, signal_1));
    } else if (recovery === 'refresh_session') options.onSessionRefresh();else {
      setChallengeUrl(null);
      if (next.phase === 'reconciling') setTransactionId(next.transactionId);else setError(next.phase);
      navigation.transition(recovery === 'status' ? 'payment_uncertain' : recovery === 'connect' ? 'connection_required' : recovery === 'review' ? 'verification_pending' : 'edit_amount');
    }
  }
  function retireInlinePayment() {
    // Revoke the local frame before requesting another amount. The old reservation
    // remains reconcilable: disposing a frame is not proof of provider cancellation.
    active.current?.abort();
    active.current = null;
    setBusy(false);
    paymentClosed.current = true;
    quoteContinuation.current = null;
    setFrameReady(false);
    setPaymentSignature(null);
    setAttemptId(null);
    setOperation(null);
    pendingCreate.current = null;
    navigation.trackOperation(null);
    setQuote(null);
    setReview(null);
    setError(null);
  }
  function editInlineAmount(units) {
    if (navigation.getSnapshot().attempted || units === amountUnits) return;
    if (attemptId && paymentSignature && operation) {
      active.current?.abort();
      active.current = null;
      setBusy(false);
      setError(null);
      setProviderFailure(null);
    } else retireInlinePayment();
    updateFundingAmount(progress, currency, decimals, setPreviewUnits)(units);
    navigation.transition('edit_amount');
  }
  function paymentEvent(event) {
    if (method_0 === 'card' && attemptId && !navigation.getSnapshot().attempted) navigation.markAttempt();
    if (paymentClosed.current && event.kind !== 'complete') return;
    if (event.kind === 'buttonPressed') {
      // A provider tap wins over a background refresh already in flight.
      active.current?.abort();
      active.current = null;
      setBusy(false);
    }
    const next_0 = applyPaymentEvent(event);
    if (next_0.phase === 'quote_expired' && operation && attemptId) {
      if (event.kind === 'quoteExpired') quoteContinuation.current = event.payload.setQuote;
      if (!quoteContinuation.current) {
        continuation(next_0, 'payment');
        return;
      }
      if (state === 'amount') return;
      setReview(current_1 => current_1 ? {
        ...current_1,
        state: 'expired'
      } : null);
      navigation.transition('quote_expired');
      void run(signal_2 => quoteOperation(operation, signal_2));
      return;
    }
    continuation(next_0, 'payment');
    if (method_0 === 'sepa' && event.kind === 'complete' && event.payload.transaction.status === 'pending') navigation.transition('bank_transaction_created');
  }
  const actionContext = {
    state,
    progress,
    attemptId,
    busy,
    navigation
  };
  return {
    purchase: operation?.purchase ?? options.directPurchase?.binding ?? null,
    currency,
    decimals,
    ...fundingDisconnectActions({
      onSessionRefresh: options.onSessionRefresh,
      client,
      provider: options.provider,
      cache,
      navigation,
      state,
      attemptId,
      busy,
      active,
      setBusy,
      setError
    }),
    ...fundingLimitActions({
      ...actionContext,
      attemptId: navigation.attempted ? attemptId : null,
      beforeVerify: () => {
        if (attemptId && !navigation.getSnapshot().attempted) retireInlinePayment();
      },
      review: state === 'amount' ? preview.review : review,
      discovery: discovery.data,
      setChallengeUrl
    }),
    ...fundingProfileActions({
      ...actionContext,
      options,
      discovery,
      refreshCustomer
    }),
    verificationFromProfile: progress.verificationFromProfile,
    profileConnection: {
      ...(discovery.data ? {
        quoteContext: discovery.data
      } : {}),
      guest: discovery.data?.guest ?? false,
      canConnect: canConnectFundingProfile(discovery.data),
      verification: discovery.data?.customerVerification ?? null
    },
    customerVerification: discovery.data?.customerVerification ?? null,
    authenticationAvailable: discovery.data?.authenticationAvailable ?? false,
    location: discovery.data?.context ?? null,
    authenticationReason: secondFactor.current ? 'account_mismatch' : discovery.data?.authenticationReason ?? null,
    preview,
    amountUnits,
    editInlineAmount,
    changeInlineMethod: () => {
      if (navigation.getSnapshot().attempted) return;
      retireInlinePayment();
      cards.selectCard(null);
      if (!options.directPurchase) updateFundingAmount(progress, currency, decimals, setPreviewUnits)(null);
      setSelected(null);
      progress.method = null;
      progress.verificationRequested = false;
      navigation.transition('change_method');
    },
    amountLocked: navigation.attempted,
    paymentFrameReady: updateQuote => {
      paymentClosed.current = false;
      // Both hosts expose the mounted frame command; a state setter is not a bridge ACK.
      quoteContinuation.current = updateQuote ?? null;
      setFrameReady(Boolean(updateQuote));
    },
    previewAmount: updateFundingAmount(progress, currency, decimals, setPreviewUnits),
    correctQuoteAmount: () => correctFundingAmount({
      state,
      preview,
      amountUnits,
      progress,
      update: updateFundingAmount(progress, currency, decimals, setPreviewUnits)
    }),
    canContinueAmount,
    loading: discovery.isPending || refreshingCustomer,
    busy,
    providerFailure: lastProviderFailure ?? providerFailure(discovery.error),
    returningMethodUnavailable: !selected && !operation && discovery.data ? unavailablePreference(discovery.data, options.deviceMethods, options.manifest) : null,
    error: error ?? (discovery.error ? providerFailure(discovery.error)?.category ?? 'provider_unavailable' : null),
    state,
    methods,
    unavailableBankMethods: discovery.data?.unavailableBankMethods ?? [],
    unavailableMethods: discovery.data?.unavailableMethods ?? [],
    savedCards,
    selectedCard: cards.card,
    selection: method_0 ? cards.card && cards.previouslyUsed && method_0 === 'card' ? {
      kind: 'saved_card',
      method: 'card',
      paymentMethodId: cards.card.id
    } : {
      kind: 'method',
      method: method_0
    } : null,
    cardEntryEvent: cards.cardEntryEvent,
    cardEntryActive: cards.cardEntryActive,
    selectSavedCard: id_0 => {
      const card = savedCards.find(candidate => candidate.id === id_0 && candidate.availability.active);
      if (!card || busy || attemptId) return;
      cards.selectCard(card);
      setSelected('card');
      progress.method = 'card';
      if (!options.directPurchase) setPreviewUnits(null);
      navigation.transition('method_selected');
    },
    deleteSavedCard: async id_1 => {
      await cards.deleteCard(id_1, savedCards);
      await discovery.refetch();
    },
    method: method_0,
    operation,
    pendingId,
    quote,
    review,
    attemptId,
    paymentSignature,
    challengeUrl,
    canResumeChallenge: challengeRetryUrl !== null,
    resumeChallenge: () => run(async signal_3 => {
      if (!operation || !challengeRetryUrl) return;
      const current_2 = fundingOperationForCheckout((await client.getOperation(operation.id, signal_3)).data);
      if (signal_3.aborted) return;
      setOperation(current_2);
      navigation.trackOperation(current_2.id);
      if (['delivered', 'failed', 'cancelled', 'expired', 'refunded'].includes(current_2.status)) {
        setChallengeRetryUrl(null);
        navigation.transition('payment_uncertain');
        return;
      }
      setChallengeUrl(challengeRetryUrl);
      navigation.transition('payment_challenge');
    }),
    transactionId,
    select: next_1 => {
      if (!busy && !attemptId && methods.includes(next_1)) {
        cards.selectCard(null);
        if (!options.directPurchase) {
          setPreviewUnits(null);
          progress.amountUnits = null;
        }
        setQuote(null);
        setReview(null);
        progress.verificationRequested = false;
        setSelected(next_1);
        progress.method = next_1;
        navigation.transition('method_selected');
      }
    },
    ...createCheckoutIntentActions({
      client,
      progress,
      navigation,
      method: method_0,
      attemptId,
      operation,
      discovery,
      state,
      preview,
      canContinueAmount,
      quoteContinuation,
      pendingCreate,
      pendingCardMount,
      options,
      currency,
      decimals,
      surface,
      setOperation,
      setSelected,
      setQuote,
      setReview,
      setPreviewUnits,
      quoteOperation,
      run,
      paymentClosed,
      paymentSignature,
      quote,
      review,
      setError,
      setPaymentSignature,
      setAttemptId,
      approveQuote,
      presentQuote,
      setChallengeUrl,
      ...(cards.card ? {
        providerPaymentMethodId: cards.card.id
      } : {}),
      continueCard: cards.continueCard
    }),
    retry: () => {
      if (state === 'verification' && !attemptId && !paymentSignature) {
        // Contact attestation is bound at session creation, not connection rediscovery.
        progress.verificationRequested = false;
        setError(null);
        setProviderFailure(null);
        options.onSessionRefresh();
        return;
      }
      if (state === 'status' && operation && attemptId) {
        // An ambiguous automatically executing frame must only refresh its
        // existing operation; never mint a replacement payment from a 404.
        void run(async signal_4 => {
          const refreshed = (await client.getOperation(operation.id, signal_4)).data;
          if (signal_4.aborted) return;
          setOperation(fundingOperationForCheckout(refreshed));
          if (!['delivered', 'failed', 'cancelled', 'expired', 'refunded'].includes(refreshed.status)) setError('payment_page_load_failed');
        });
        return;
      }
      if ((state === 'connect' || state === 'profile') && !attemptId && !paymentSignature && !navigation.getSnapshot().attempted) {
        // Location refresh must replace the session, not rediscover its cached location.
        options.onSessionRefresh();
        return;
      }
      const pendingMount = pendingCardMount.current;
      if (pendingMount) {
        void run(signal_5 => pendingMount(signal_5, false));
        return;
      }
      if ((state === 'methods' || state === 'connection_required') && (discovery.data?.authenticationReason === 'guest_unavailable' || discovery.data?.authenticationReason === 'region' || discovery.data?.authenticationReason === 'location_unavailable')) {
        options.onSessionRefresh();
        return;
      }
      if (state === 'card_entry') {
        setError(null);
        cards.retryCardEntry();
        return;
      }
      if (operation?.receiveTarget && !fundingRateIsCurrent(operation.receiveTarget.fx, Date.now()) && !navigation.getSnapshot().attempted) {
        const units_0 = operation.receiveTarget.localUnits;
        retireInlinePayment();
        updateFundingAmount(progress, currency, decimals, setPreviewUnits)(units_0);
        navigation.transition('edit_amount');
        return;
      }
      if (returnFromLimitVerification(progress, navigation, attemptId, true)) {
        refreshCustomer();
        return;
      }
      if (state === 'customer_verification' && returnToFundingProfile(progress, navigation, attemptId)) {
        setError(null);
        refreshCustomer();
        navigation.transition('profile_requested');
        return;
      }
      if (state === 'customer_verification') {
        navigation.transition('verification_complete');
        refreshCustomer();
        return;
      }
      if ((state === 'review' || state === 'amount') && attemptId && method_0 && isHostedFundingMethod(method_0) && !paymentSignature) {
        setError(null);
        setProviderFailure(null);
        navigation.transition('payment_uncertain');
        return;
      }
      if (discovery.isError && !operation && !attemptId && !paymentSignature) {
        setError(null);
        setProviderFailure(null);
        options.onSessionRefresh();
        return;
      }
      if (state === 'amount' && preview.error === 'provider_unavailable' && !operation && !attemptId && !paymentSignature) {
        setError(null);
        setProviderFailure(null);
        preview.retry();
        refreshCustomer();
        return;
      }
      setError(null);
      setProviderFailure(null);
      preview.retry();
      void flushPaymentReport();
      if (operation && (quoteContinuation.current || state === 'review' && !attemptId)) void run(signal_6 => quoteOperation(operation, signal_6));else void discovery.refetch();
    },
    connect: () => {
      if (preview.providerFailure?.category === 'connection_required' || lastProviderFailure?.category === 'connection_required') forgetFundingCustomer(provider);
      if (secondFactor.current && operation && attemptId && !paymentClosed.current) {
        void run(async signal_7 => {
          const current_3 = (await client.getOperation(operation.id, signal_7)).data;
          if (signal_7.aborted) return;
          if (current_3.status === 'submission_unknown' && !current_3.providerReference) navigation.transition('connect_requested');else {
            paymentClosed.current = true;
            setPaymentSignature(null);
            navigation.transition('payment_uncertain');
          }
        });
        return;
      }
      if (attemptId) navigation.transition('payment_uncertain');else if (discovery.data?.authenticationAvailable || discovery.data?.guest && discovery.data.methods.length > 0) navigation.transition('connect_requested');else {
        setError(null);
        setProviderFailure(null);
        navigation.transition('session_refreshed');
        void discovery.refetch();
      }
    },
    connected: (status = 'active', customerId) => {
      if (status === 'active' && customerId) rememberFundingCustomer(provider, customerId);
      if (operation && attemptId && status === 'active') void verifyRequiredFundingAction(client, provider, operation.id);
      if (secondFactor.current && operation && attemptId && status === 'active') {
        void run(async signal_8 => {
          const current_4 = (await client.getOperation(operation.id, signal_8)).data;
          if (signal_8.aborted) return;
          secondFactor.current = false;
          if (current_4.status === 'submission_unknown' && !current_4.providerReference && !paymentClosed.current) {
            setError(null);
            navigation.transition('attempt_executable');
          } else {
            paymentClosed.current = true;
            setPaymentSignature(null);
            navigation.transition('payment_uncertain');
          }
        });
        return;
      }
      if (status === 'active' && (returnFromLimitVerification(progress, navigation, attemptId, true) || returnToFundingProfile(progress, navigation, attemptId))) {
        refreshCustomer();
        return;
      }
      const event_0 = authenticatedCheckoutEvent(status, Boolean(attemptId));
      if (status === 'active' && operation?.method === 'card' && !attemptId) {
        void run(async signal_9 => {
          await discovery.refetch();
          if (!signal_9.aborted) await cards.continueCard(operation, signal_9);
        });
        return;
      }
      navigation.transition(event_0);
      if (event_0 === 'authentication_complete') refreshCustomer();
    },
    paymentEvent,
    hostedEvent(event_1) {
      // Retain transaction identity after the report is flushed. Repeated callbacks
      // must not restart reporting or regress a terminal surface back to payment.
      if (event_1.kind === 'transactionCreated' && providerTransaction.current === event_1.payload.transaction.id) return;
      if (paymentClosed.current && event_1.kind !== 'complete' && event_1.kind !== 'transactionCreated') return;
      if (event_1.kind === 'complete' && paymentClosed.current && providerTransaction.current === event_1.payload.transaction.id) return;
      continuation(applyHostedWidgetEvent(event_1), 'payment');
      if (event_1.kind === 'transactionCreated' && state !== 'payment') {
        paymentClosed.current = true;
        setPaymentSignature(null);
        navigation.transition('payment_uncertain');
      }
    },
    viewPaymentStatus() {
      paymentClosed.current = true;
      setPaymentSignature(null);
      navigation.transition('payment_uncertain');
    },
    paymentPageLoadFailed() {
      if (navigation.getSnapshot().attempted || providerTransaction.current) {
        paymentClosed.current = true;
        setPaymentSignature(null);
        navigation.transition('payment_uncertain');
        return;
      }
      retireInlinePayment();
      navigation.transition('edit_amount');
    },
    paymentFrameDetached: () => {
      setFrameReady(false);
      if (navigation.getSnapshot().checkout === 'bank_transfer_instructions') return;
      if (navigation.getSnapshot().attempted && attemptId) {
        paymentClosed.current = true;
        setPaymentSignature(null);
        quoteContinuation.current = null;
        active.current?.abort();
        setBusy(false);
        navigation.transition('payment_uncertain');
        return;
      }
      if (!quoteContinuation.current) return;
      paymentClosed.current = true;
      quoteContinuation.current = null;
      active.current?.abort();
      setBusy(false);
      setPaymentSignature(null);
      setError('quote_expired');
      navigation.transition('payment_unavailable');
    },
    challengeEvent: event_2 => {
      if (handleLimitChallenge(event_2, actionContext, options.onSessionRefresh, setError, refreshCustomer)) return;
      if (operation && attemptId && event_2.kind === 'complete') void verifyRequiredFundingAction(client, provider, operation.id);
      if (event_2.kind === 'complete') setChallengeRetryUrl(null);else if (attemptId && challengeUrl && (event_2.kind === 'cancelled' && event_2.payload.flow === 'buy' || event_2.kind === 'error' && event_2.payload.code !== 'customer_status_unavailable')) setChallengeRetryUrl(challengeUrl);
      continuation(applyChallengeEvent(event_2), 'challenge');
      if (method_0 === 'sepa' && event_2.kind === 'complete' && event_2.payload.flow === 'buy' && event_2.payload.transaction.status === 'pending') navigation.transition('bank_transaction_created');
    }
  };
}